/**
 * «Данные → Аудио → Сегменты» — непрерывный поток клипов за сутки с полосой границ диалогов (владелец 14.09, L3;
 * разбор 15.09, T1–T5 в services/plugins/speech_to_text/TODO.md).
 *
 * Слева полоса диалогов. Прод-разговоры — рамки по умолчанию, их края сразу тянутся мышью; первая же
 * правка прод-рамки создаёт её копию, которая дальше заменяет прод-разговор (и в «Диалогах» тоже).
 * Рамки перекрываются (два гостя вперемешку); новая рамка — кнопкой у клипа. Хранится отдельно от
 * прода (DialogueBoundaryMarkup), прод не меняется.
 *
 * Клип раскрывается в плеер с волной и участками расшифровки и в поле текста: исправленный текст
 * пишется в ту же строку пула, что «Текст клипов» (AudioLabel), — отдельная вкладка для этого не нужна.
 */
import { Select } from '@/components/ui/select';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { Check, ChevronDown, ChevronRight, Loader2, Plus, RotateCcw, Save, Trash2, Undo2, VolumeX } from 'lucide-react';
import {
  getDialogueBoundaries, saveDialogueBoundaries, saveClipLabel,
  type AudioRecordRaw, type DialogueBoundary,
} from '@/api/conversations';
import { getAudioPoolZones } from '@/api/audioPool';
import { Button } from '@/components/ui/button';
import { loadDay, loadDayConversations, mergeDialogues, t0, t1, type DayDialogue } from './dayStream';
import ClipPlayer from './ClipPlayer';
import { Tooltip } from '@/components/ui/tooltip';

const COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#0ea5e9', '#a855f7', '#84cc16', '#ec4899'];
const ROW_H = 40;
const NO_CLIPS: AudioRecordRaw[] = [];
const LANE_W = 12;
const uid = () => Math.random().toString(36).slice(2, 10);
const todayMsk = () => new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10);
const mskTime = (iso: string | number) =>
  new Date(iso).toLocaleTimeString('ru-RU', { timeZone: 'Europe/Moscow', hour: '2-digit', minute: '2-digit', second: '2-digit' });

/** Статусы клипа словами — те же подписи, что в «Сырых данных» на вкладке разговоров. */
const STATUS_RU: Record<string, string> = {
  PENDING: 'В обработке',
  RECORDING: 'Запись',
  TRANSCRIBED: 'Расшифрован',
  ANALYZED: 'Проанализирован',
  NO_SPEECH: 'Нет речи',
  NO_AUDIO: 'Нет аудио',
  NOT_CONVERSATION: 'Не клиентский разговор',
  PARTIAL: 'Частичная запись',
  FAILED: 'Ошибка',
}

type RowFilter = 'all' | 'prod' | 'human' | 'text' | 'dialogue';
const FILTERS: { v: RowFilter; label: string }[] = [
  { v: 'all', label: 'все клипы' },
  { v: 'prod', label: 'неизменные с прода' },
  { v: 'human', label: 'размечено человеком' },
  { v: 'text', label: 'исправлен только текст' },
  { v: 'dialogue', label: 'текст нанесён на диалог' },
];

function lanes<T extends { a: number; b: number }>(items: T[]): { item: T; lane: number }[] {
  const ends: number[] = [];
  return [...items].sort((x, y) => x.a - y.a).map((item) => {
    let lane = ends.findIndex((e) => e <= item.a);
    if (lane < 0) { lane = ends.length; ends.push(item.b); } else ends[lane] = item.b;
    return { item, lane };
  });
}

export default function SegmentStream() {
  const { data: zonesResp, error: zonesErr, mutate: retryZones } = useSWR('audio-pool-zones', () => getAudioPoolZones().then((r) => r.data));
  const zones = zonesResp?.zones ?? [];
  const [zoneId, setZoneId] = useState<number | null>(null);
  const [day, setDay] = useState(todayMsk());
  useEffect(() => { if (zoneId == null && zones.length) setZoneId(zones[0].zone_id); }, [zones, zoneId]);

  const { data: clips, isLoading, error: clipsErr, mutate: mutateClips } = useSWR(zoneId == null ? null : ['markup-day', zoneId, day],
    () => loadDay(zoneId as number, day), { revalidateOnFocus: false });
  const { data: saved, mutate: mutateSaved } = useSWR(zoneId == null ? null : ['seg-bounds', zoneId, day],
    () => getDialogueBoundaries(zoneId as number, day), { revalidateOnFocus: false });
  const { data: convs } = useSWR(zoneId == null ? null : ['markup-day-convs', zoneId, day],
    () => loadDayConversations(zoneId as number, day), { revalidateOnFocus: false });

  const [items, setItems] = useState<DialogueBoundary[]>([]);
  const [history, setHistory] = useState<DialogueBoundary[][]>([]);
  const [sel, setSel] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [msg, setMsg] = useState('');
  const [filter, setFilter] = useState<RowFilter>('all');
  useEffect(() => { if (saved) { setItems(saved.items); setHistory([]); } }, [saved]);
  const dirty = !!saved && JSON.stringify(items) !== JSON.stringify(saved.items);

  // Каждая правка кладёт прошлое состояние в историю: Ctrl/Cmd+Z шаг назад, «отменить правки» — к сохранённому.
  const commit = useCallback((fn: (xs: DialogueBoundary[]) => DialogueBoundary[]) => {
    setItems((xs) => { setHistory((h) => [...h.slice(-99), xs]); return fn(xs); });
  }, []);
  const undo = () => setHistory((h) => { if (!h.length) return h; setItems(h[h.length - 1]); return h.slice(0, -1); });
  const revert = () => { if (saved) { setItems(saved.items); setHistory([]); setSel(null); } };

  const allRows = clips ?? NO_CLIPS;
  const dialogues = useMemo(() => mergeDialogues(allRows, items), [allRows, items]);

  // Где разметка уже нанесена на диалог: у прод-разговора — события в его разметке, у рамки — в самой рамке.
  const markedConv = useMemo(() => new Set((convs ?? []).filter((c) => (c.markup?.events ?? 0) > 0).map((c) => c.id)), [convs]);
  const markedSpans = useMemo(() => dialogues.filter((d) => (d.item ? ((d.item as DialogueBoundary & { events?: unknown[] }).events?.length ?? 0) > 0
    : d.convId != null && markedConv.has(d.convId))).map((d) => [d.a, d.b] as const), [dialogues, markedConv]);
  const changedSpans = useMemo(() => dialogues.filter((d) => d.origin !== 'prod').map((d) => [d.a, d.b] as const), [dialogues]);
  const inSpans = (c: AudioRecordRaw, spans: readonly (readonly [number, number])[]) =>
    !!c.recorded_at && spans.some(([a, b]) => t1(c) > a && t0(c) < b);
  const textFixed = (c: AudioRecordRaw) => c.label?.corrected_text != null && c.label.source === 'user';

  const rows = useMemo(() => allRows.filter((c) => {
    if (filter === 'all') return true;
    const onDialogue = inSpans(c, markedSpans);
    if (filter === 'prod') return !textFixed(c) && !onDialogue && !inSpans(c, changedSpans);
    if (filter === 'human') return textFixed(c) || onDialogue;
    if (filter === 'text') return textFixed(c) && !onDialogue;
    return onDialogue;
  }), [allRows, filter, markedSpans, changedSpans]);

  // Геометрия: время → y по заголовкам видимых строк (раскрытые строки только растягивают промежуток).
  const listRef = useRef<HTMLDivElement | null>(null);
  const headRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [tops, setTops] = useState<number[]>([]);
  useLayoutEffect(() => {
    const base = listRef.current?.getBoundingClientRect().top ?? 0;
    const next = headRefs.current.slice(0, rows.length).map((el) => (el ? el.getBoundingClientRect().top - base : 0));
    setTops((prev) => (prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next));
  }, [rows, open]);
  const yOf = (ms: number) => {
    if (!rows.length || tops.length !== rows.length) return 0;
    if (ms <= t0(rows[0])) return tops[0];
    for (let i = 0; i < rows.length; i++) {
      const a = t0(rows[i]), b = t1(rows[i]);
      if (ms < a) return tops[i];                       // разрыв записи — к началу следующего клипа
      if (ms <= b) return tops[i] + ((ms - a) / (b - a)) * ROW_H;
    }
    return tops[rows.length - 1] + ROW_H;
  };
  const msOf = (y: number) => {
    for (let i = rows.length - 1; i >= 0; i--) {
      if (y >= tops[i]) {
        const f = Math.min(1, Math.max(0, (y - tops[i]) / ROW_H));
        return t0(rows[i]) + f * (t1(rows[i]) - t0(rows[i]));
      }
    }
    return rows.length ? t0(rows[0]) : 0;
  };

  const band = useMemo(() => lanes(dialogues), [dialogues]);
  const nLanes = Math.max(1, ...band.map((x) => x.lane + 1));
  const bandW = nLanes * LANE_W + 8;

  /** Рамка, которую правим: у прод-разговора — его копия (создаётся при первой правке). */
  const editable = (d: DayDialogue): string => {
    if (d.item) return d.item.id;
    const it: DialogueBoundary = { id: uid(), start: new Date(d.a).toISOString(), end: new Date(d.b).toISOString(),
      color: COLORS[items.length % COLORS.length], comment: '', conversation_id: d.convId };
    commit((xs) => [...xs, it]);
    return it.id;
  };
  const patch = (id: string, p: Partial<DialogueBoundary>) => commit((xs) => xs.map((x) => (x.id === id ? { ...x, ...p } : x)));
  const add = (a: number, b: number) => {
    const it: DialogueBoundary = { id: uid(), start: new Date(a).toISOString(), end: new Date(b).toISOString(),
      color: COLORS[items.length % COLORS.length], comment: '', conversation_id: null };
    commit((xs) => [...xs, it]); setSel(`item-${it.id}`);
  };

  const drag = useRef<{ id: string; edge: 'a' | 'b' } | null>(null);
  useEffect(() => {
    const move = (e: PointerEvent) => {
      const d = drag.current; if (!d || !listRef.current) return;
      const ms = msOf(e.clientY - listRef.current.getBoundingClientRect().top);
      setItems((xs) => xs.map((x) => {
        if (x.id !== d.id) return x;
        const a = Date.parse(x.start), b = Date.parse(x.end);
        if (d.edge === 'a') return { ...x, start: new Date(Math.min(ms, b - 500)).toISOString() };
        return { ...x, end: new Date(Math.max(ms, a + 500)).toISOString() };
      }));
    };
    const up = () => { drag.current = null; };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
  });
  const startDrag = (d: DayDialogue, edge: 'a' | 'b') => {
    const id = editable(d);
    // История: край тянется много кадров подряд — в историю кладём одно состояние до начала.
    if (d.item) setHistory((h) => [...h.slice(-99), items]);
    drag.current = { id, edge }; setSel(`item-${id}`);
  };

  const save = async () => {
    if (zoneId == null) return;
    try { const r = await saveDialogueBoundaries(zoneId, day, items); await mutateSaved(r, { revalidate: false }); setHistory([]); setMsg('сохранено'); }
    catch { setMsg('не сохранилось'); }
  };
  const onKey = (e: React.KeyboardEvent) => {
    const tag = (e.target as HTMLElement).tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); undo(); }
  };

  const cur = dialogues.find((d) => d.key === sel);

  return (
    <div className="space-y-3 outline-none" tabIndex={-1} onKeyDown={onKey}>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <Select className="h-8" wrapperClassName="w-auto" value={zoneId ?? ''} onChange={(e) => setZoneId(Number(e.target.value))}>
          {zones.map((z) => <option key={z.zone_id} value={z.zone_id}>{z.zone || `зона ${z.zone_id}`}</option>)}
        </Select>
        <input type="date" className="rounded-md border bg-background px-2 py-1" value={day} onChange={(e) => setDay(e.target.value)} />
        <Select className="h-8" wrapperClassName="w-auto" value={filter} onChange={(e) => setFilter(e.target.value as RowFilter)}>
          {FILTERS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
        </Select>
        {isLoading && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
        <span className="text-muted-foreground">
          клипов {rows.length}{rows.length !== allRows.length ? ` из ${allRows.length}` : ''} · диалогов {dialogues.length}
          {' '}(изменено {dialogues.filter((d) => d.origin !== 'prod').length})
        </span>
        <span className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{msg && !dirty ? msg : dirty ? 'есть несохранённые правки' : saved?.updated_by ? `сохранил ${saved.updated_by}` : ''}</span>
          <Button size="sm" variant="outline" onClick={undo} disabled={!history.length} title="Шаг назад (Ctrl/Cmd+Z)"><Undo2 className="size-3.5" /></Button>
          <Button size="sm" variant="outline" onClick={revert} disabled={!dirty} title="Вернуть как сохранено"><RotateCcw className="size-3.5" /> отменить правки</Button>
          <Button size="sm" onClick={() => void save()} disabled={!dirty}><Save className="size-3.5" /> сохранить</Button>
        </span>
      </div>
      {(zonesErr || clipsErr) && (
        <div className="flex items-center gap-2 rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
          {zonesErr ? 'Список зон не загрузился.' : 'Поток клипов не загрузился.'}
          <Button size="sm" variant="outline" onClick={() => void (zonesErr ? retryZones() : mutateClips())}>повторить</Button>
        </div>
      )}
      {cur && (
        <div className="flex flex-wrap items-center gap-2 rounded border bg-card p-2 text-xs">
          <span className="inline-block size-3 rounded-sm" style={{ background: cur.item?.color || '#94a3b8' }} />
          {mskTime(cur.a)} – {mskTime(cur.b)}
          <span className="text-muted-foreground">
            {cur.origin === 'prod' ? `прод #${cur.convId}` : cur.origin === 'changed' ? `изменён прод #${cur.convId}` : 'новый диалог'}
          </span>
          {(['start', 'end'] as const).map((k) => (
            <span key={k} className="inline-flex items-center gap-0.5">
              {k === 'start' ? 'начало' : 'конец'}
              {[-1000, 1000].map((dt) => (
                <button key={dt} type="button" className="rounded border px-1" onClick={() => {
                  const id = editable(cur);
                  setItems((xs) => xs.map((x) => (x.id === id ? { ...x, [k]: new Date(Date.parse(x[k]) + dt).toISOString() } : x)));
                  setSel(`item-${id}`);
                }}>{dt < 0 ? '−1с' : '+1с'}</button>
              ))}
            </span>
          ))}
          {cur.item && (
            <input className="min-w-60 flex-1 rounded border px-2 py-0.5" placeholder="комментарий к рамке" value={cur.item.comment}
              onChange={(e) => patch((cur.item as DialogueBoundary).id, { comment: e.target.value })} />
          )}
          {cur.origin === 'changed' && (
            <Button size="sm" variant="ghost" title="Убрать правку — диалог снова как в проде"
              onClick={() => { commit((xs) => xs.filter((x) => x.id !== cur.item?.id)); setSel(`conv-${cur.convId}`); }}>
              <RotateCcw className="size-3" /> как в проде
            </Button>
          )}
          <Button size="sm" variant="ghost" title="Удалить диалог"
            onClick={() => {
              if (cur.origin === 'new') commit((xs) => xs.filter((x) => x.id !== cur.item?.id));
              else { const id = editable(cur); setItems((xs) => xs.map((x) => (x.id === id ? { ...x, deleted: true } : x))); }
              setSel(null);
            }}>
            <Trash2 className="size-3" />
          </Button>
        </div>
      )}
      <div ref={listRef} className="relative rounded-lg border bg-card" style={{ paddingLeft: bandW }}>
        {/* полоса диалогов: серые — прод как есть, цветные — изменённые и новые */}
        <div className="absolute inset-y-0 left-0 border-r bg-muted/30" style={{ width: bandW }}>
          {band.map(({ item: d, lane }) => {
            const color = d.item?.color || '#94a3b8';
            return (
              <Tooltip key={d.key} content={`${d.origin === 'prod' ? `прод #${d.convId}` : d.origin === 'changed' ? `изменён прод #${d.convId}` : 'новый'}: ${mskTime(d.a)}–${mskTime(d.b)}${d.item?.comment ? ` · ${d.item.comment}` : ''}`}><div onClick={() => setSel(d.key)}
                className={`absolute cursor-pointer rounded-sm ${d.origin === 'prod' ? 'border border-foreground/30' : 'border-2'} ${sel === d.key ? 'ring-2 ring-primary' : ''}`}
                style={{ left: 3 + lane * LANE_W, width: LANE_W - 3, top: yOf(d.a), height: Math.max(6, yOf(d.b) - yOf(d.a)),
                  borderColor: d.origin === 'prod' ? undefined : color, background: `${color}${d.origin === 'prod' ? '40' : '33'}` }}>
                <span className="absolute inset-x-0 top-0 h-1.5 cursor-ns-resize" style={{ background: color }}
                  onPointerDown={(e) => { e.stopPropagation(); startDrag(d, 'a'); }} />
                <span className="absolute inset-x-0 bottom-0 h-1.5 cursor-ns-resize" style={{ background: color }}
                  onPointerDown={(e) => { e.stopPropagation(); startDrag(d, 'b'); }} />
              </div></Tooltip>
            );
          })}
        </div>
        {rows.map((c, i) => {
          const gapMin = i > 0 ? (t0(c) - t1(rows[i - 1])) / 60000 : 0;
          const isOpen = open.has(c.id);
          return (
            <div key={c.id}>
              {gapMin > 0.1 && (
                <div className="border-t border-dashed px-2 text-[10px] text-muted-foreground">
                  {filter === 'all' ? `разрыв записи ${gapMin.toFixed(1)} мин` : `${gapMin.toFixed(1)} мин не в фильтре или без записи`}
                </div>
              )}
              <div ref={(el) => { headRefs.current[i] = el; }} style={{ height: ROW_H }}
                className="flex items-center gap-2 border-t px-2 text-xs hover:bg-muted/40">
                <button type="button" onClick={() => setOpen((s) => { const n = new Set(s); if (n.has(c.id)) n.delete(c.id); else n.add(c.id); return n; })}>
                  {isOpen ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
                </button>
                <span className="font-mono tabular-nums">{mskTime(c.recorded_at as string)}</span>
                <span className="w-10 text-muted-foreground">{(c.clip_duration || 0).toFixed(0)} с</span>
                {/* Раньше здесь стоял сырой код статуса (NOT_CONVERSATION): в разделе
                    «Сырые данные» на вкладке разговоров он подписан словами, и админка
                    не должна быть беднее (Алексей 16.09). */}
                <Tooltip content={c.status}><span className="w-32 shrink-0 truncate text-[10px] text-muted-foreground">
                  {STATUS_RU[c.status] ?? c.status}{c.conversation ? ` → разговор #${c.conversation}` : ''}
                </span></Tooltip>
                {textFixed(c) && <Check className="size-3.5 shrink-0 text-green-600" aria-label="текст исправлен человеком" />}
                <span className="min-w-0 flex-1 truncate">{textFixed(c) ? (c.label?.corrected_text || '‹шум›') : c.transcription_text}</span>
                <Tooltip content="номер клипа"><span className="shrink-0 whitespace-nowrap text-[10px] tabular-nums text-muted-foreground">
                  клип #{c.id}
                </span></Tooltip>
                <Tooltip content="новая рамка на этот клип"><button type="button" aria-label="новая рамка на этот клип" className="rounded border px-1 text-muted-foreground hover:text-foreground"
                  onClick={() => add(t0(c), t1(c))}><Plus className="size-3" /></button></Tooltip>
              </div>
              {isOpen && <ClipRow clip={c} onSaved={() => void mutateClips()} />}
            </div>
          );
        })}
        {!rows.length && !isLoading && (
          <div className="px-3 py-6 text-sm text-muted-foreground">{allRows.length ? 'В этом фильтре клипов нет' : 'Клипов за этот день нет'}</div>
        )}
      </div>
    </div>
  );
}

/** Раскрытый клип: плеер с волной и участками, текст клипа с сохранением в пул. */
function ClipRow({ clip, onSaved }: { clip: AudioRecordRaw; onSaved: () => void }) {
  const initial = clip.label?.corrected_text ?? clip.transcription_text ?? '';
  const [text, setText] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const labelled = clip.label?.corrected_text != null;
  const touched = text !== initial;
  const put = async (v: string | null) => {
    setBusy(true); setNote('');
    try { await saveClipLabel(clip.id, v); setNote(v === null ? 'возвращён в очередь' : 'сохранено'); onSaved(); }
    catch { setNote('не сохранилось'); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 bg-muted/20 px-8 py-2 text-xs">
      {/* Точный интервал с секундами: по времени до минуты не понять, идут ли
          соседние клипы стык в стык или между ними дыра, а дыры тут и есть
          главный вопрос. Тот же показатель есть в «Сырых данных». */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        <span className="font-mono tabular-nums">
          {mskTime(clip.recorded_at as string)} — {mskTime(Date.parse(String(clip.recorded_at)) + (clip.clip_duration || 0) * 1000)}
        </span>
        <span>длительность {(clip.clip_duration || 0).toFixed(1)} с</span>
        <span>клип #{clip.id}</span>
        {clip.conversation != null && <span>относится к разговору #{clip.conversation}</span>}
      </div>
      {clip.audio_url ? <ClipPlayer recId={clip.id} segments={clip.transcript_segments ?? []} />
        : <div className="text-muted-foreground">звука у клипа нет</div>}
      {(clip.transcript_segments ?? []).length > 0 && (
        <div className="space-y-0.5">
          {(clip.transcript_segments ?? []).map((seg, i) => (
            <div key={i} className="flex gap-2">
              <span className="w-20 shrink-0 font-mono tabular-nums text-[10px] text-muted-foreground">
                {seg.start.toFixed(1)}–{seg.end.toFixed(1)}
              </span>
              <span className={seg.text ? '' : 'text-muted-foreground'}>
                {seg.text || `‹${seg.gap ?? 'пауза'}${seg.db != null ? `, ${seg.db.toFixed(1)} дБ` : ''}›`}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-col gap-1">
        <textarea className="min-h-12 w-full rounded border bg-background px-2 py-1 text-sm" value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); void put(text); } }}
          placeholder="что сказано в клипе, дословно" />
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="secondary" disabled={busy || (labelled && !touched)} onClick={() => void put(text)}>
            {busy && <Loader2 className="size-3 animate-spin" />} {!labelled && !touched ? 'принять текст прода' : 'сохранить текст'}
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => void put('')} title="В клипе шум или ничего не разобрать">
            <VolumeX className="size-3.5" /> шум
          </Button>
          {labelled && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => void put(null)}><Undo2 className="size-3.5" /> вернуть в очередь</Button>
          )}
          <span className="text-muted-foreground">{note || (labelled ? `исправил ${clip.label?.annotated_by ?? '—'}` : 'текст прода, человек не проверял')}</span>
          <span className="ml-auto text-muted-foreground">прод: {clip.transcription_text || '—'}</span>
        </div>
      </div>
    </div>
  );
}
